import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import '../Markets.css';

const Forex: React.FC = () => {
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: { 
      opacity: 1,
      transition: { staggerChildren: 0.1 }
    }
  };

  const itemVariants = {
    hidden: { y: 20, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
    }
  };

  const forexPairs = [
    { category: 'Major Pairs', pairs: ['EUR/USD', 'GBP/USD', 'USD/JPY', 'USD/CHF', 'USD/CAD', 'AUD/USD', 'NZD/USD'] },
    { category: 'Minor Pairs', pairs: ['EUR/GBP', 'EUR/AUD', 'EUR/CAD', 'GBP/JPY', 'CHF/JPY', 'NZD/JPY', 'AUD/CAD'] },
    { category: 'Exotic Pairs', pairs: ['USD/TRY', 'EUR/TRY', 'USD/ZAR', 'USD/MXN', 'USD/SGD', 'EUR/NOK', 'USD/SEK'] }
  ];

  return (
    <motion.div 
      className="markets-page"
      initial="hidden"
      animate="visible"
      variants={containerVariants}
    >
      <div className="page-content">
        <motion.div className="page-title" variants={itemVariants}>
          <h1>Forex Trading</h1>
          <p>Trade the world's largest and most liquid financial market</p>
        </motion.div>

        

        <motion.section className="market-overview" variants={itemVariants}>
          <h2>What is Forex Trading?</h2>
          <p>
            The foreign exchange market (Forex or FX) is the largest financial market in the world, with a daily trading volume exceeding $7.5 trillion. 
            Forex trading involves buying one currency while simultaneously selling another, with the goal of profiting from changes in exchange rates.
          </p>
          <p>
            Unlike stock markets, the Forex market operates 24 hours a day, 5 days a week, allowing traders to respond to global economic events 
            and news in real-time. This makes it one of the most dynamic and accessible markets for traders worldwide.
          </p>
        </motion.section>

        <motion.section className="market-features" variants={itemVariants}>
          <h2>Why Trade Forex?</h2>
          <div className="features-grid">
            <motion.div className="feature" variants={itemVariants} whileHover={{ scale: 1.05 }}>
              <h3>High Liquidity</h3>
              <p>With trillions traded daily, you can enter and exit positions quickly at competitive prices</p>
            </motion.div>
            
            <motion.div className="feature" variants={itemVariants} whileHover={{ scale: 1.05 }}>
              <h3>24/5 Market Access</h3>
              <p>Trade around the clock from Sunday evening to Friday evening, fitting trading into your schedule</p>
            </motion.div>
            
            <motion.div className="feature" variants={itemVariants} whileHover={{ scale: 1.05 }}>
              <h3>Leverage Trading</h3>
              <p>Access larger positions with smaller capital through leverage (use responsibly with risk management)</p>
            </motion.div>
            
            <motion.div className="feature" variants={itemVariants} whileHover={{ scale: 1.05 }}>
              <h3>Low Transaction Costs</h3>
              <p>Typically lower spreads and commissions compared to other markets</p>
            </motion.div>
            
            <motion.div className="feature" variants={itemVariants} whileHover={{ scale: 1.05 }}>
              <h3>Diverse Trading Pairs</h3>
              <p>Choose from major, minor, and exotic currency pairs to match your trading strategy</p>
            </motion.div>
            
            <motion.div className="feature" variants={itemVariants} whileHover={{ scale: 1.05 }}>
              <h3>Profit in Both Directions</h3>
              <p>Make money whether the market goes up or down by buying or selling currency pairs</p>
            </motion.div>
          </div>
        </motion.section>

        <motion.section className="currency-pairs" variants={itemVariants}>
          <h2>Available Currency Pairs</h2>
          {forexPairs.map((group, index) => (
            <motion.div key={index} className="pair-category" variants={itemVariants}>
              <h3>{group.category}</h3>
              <div className="pairs-list">
                {group.pairs.map((pair, pairIndex) => (
                  <span key={pairIndex} className="pair-badge">{pair}</span>
                ))}
              </div>
            </motion.div>
          ))}
        </motion.section>

        <motion.section className="trading-sessions" variants={itemVariants}>
          <h2>Major Trading Sessions</h2>
          <div className="sessions-grid">
            <div className="session">
              <h3>🇦🇺 Sydney Session</h3>
              <p><strong>Time:</strong> 10:00 PM - 7:00 AM GMT</p>
              <p>Low to moderate volatility, ideal for AUD and NZD pairs</p>
            </div>
            <div className="session">
              <h3>🇯🇵 Tokyo Session</h3>
              <p><strong>Time:</strong> 12:00 AM - 9:00 AM GMT</p>
              <p>Increased liquidity for JPY pairs and Asian markets</p>
            </div>
            <div className="session">
              <h3>🇬🇧 London Session</h3>
              <p><strong>Time:</strong> 8:00 AM - 5:00 PM GMT</p>
              <p>Highest liquidity and volatility, major EUR and GBP movements</p>
            </div>
            <div className="session">
              <h3>🇺🇸 New York Session</h3>
              <p><strong>Time:</strong> 1:00 PM - 10:00 PM GMT</p>
              <p>Strong USD activity, overlaps with London for maximum volatility</p>
            </div>
          </div>
        </motion.section>

        <motion.section className="trading-tips" variants={itemVariants}>
          <h2>Forex Trading Tips</h2>
          <div className="tips-grid">
            <div className="tip">
              <h4>📊 Understand Market Analysis</h4>
              <p>Master both technical and fundamental analysis to make informed trading decisions</p>
            </div>
            <div className="tip">
              <h4>🎯 Use Risk Management</h4>
              <p>Never risk more than 1-2% of your capital on a single trade. Always use stop-loss orders</p>
            </div>
            <div className="tip">
              <h4>📰 Follow Economic News</h4>
              <p>Stay updated on economic calendars, central bank announcements, and geopolitical events</p>
            </div>
            <div className="tip">
              <h4>🧪 Start with Demo Trading</h4>
              <p>Practice your strategies on a demo account before risking real money</p>
            </div>
            <div className="tip">
              <h4>📈 Keep a Trading Journal</h4>
              <p>Document your trades to learn from successes and mistakes</p>
            </div>
            <div className="tip">
              <h4>🎓 Continue Learning</h4>
              <p>Markets evolve—stay educated through courses, webinars, and market analysis</p>
            </div>
          </div>
        </motion.section>

        <motion.div className="cta-section" variants={itemVariants}>
          <h2>Ready to Start Trading Forex?</h2>
          <p>Join UPM Traders and access professional signals, expert analysis, and comprehensive education</p>
          <div className="btn-container">
            <Link to="/services/signals" className="btn">Get Trading Signals</Link>
            <Link to="/education" className="btn secondary">Learn More</Link>
            <Link to="/ib-partners" className="btn secondary">Choose Your Broker</Link>
          </div>
        </motion.div>
      </div>
    </motion.div>
  );
};

export default Forex;
